import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
  output,
} from '@angular/core';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ActionMenuItem } from '@seahorse/ui';

/**
 * Bulk-action bar for the data table pattern (design handoff, "Step 5,
 * streamlined", section 3): a navy bar at the bottom of the table card while
 * rows are selected. Renders only when `count > 0`. Actions reuse the
 * `ActionMenuItem` model (@seahorse/ui), with the selected rows as context,
 * resolved the same way `sh-action-menu` resolves label/visible/disabled.
 *
 * No Shift-click range selection and no hint for it (decided: styling only).
 */
@Component({
  selector: 'sh-bulk-bar',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, TranslateModule],
  templateUrl: './bulk-bar.component.html',
})
export class BulkBarComponent<T = unknown> {
  count = input.required<number>();
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  actions = input<ActionMenuItem<T[]>[]>([]);
  context = input<T[]>([]);

  readonly clear = output<void>();

  protected readonly translate = inject(TranslateService);

  // Computed, not a method: a method returning a new filtered array would
  // feed *ngFor a fresh array on every change detection and could freeze the
  // page (see the project's "no getters/methods feeding *ngFor" rule).
  protected readonly visibleActions = computed(() => {
    const context = this.context();
    return this.actions().filter((action) =>
      this._resolve(action.visible, context, true)
    );
  });

  protected isDisabled(action: ActionMenuItem<T[]>): boolean {
    return this._resolve(action.disabled, this.context(), false);
  }

  protected label(action: ActionMenuItem<T[]>): string {
    const context = this.context();
    const label =
      typeof action.label === 'function' ? action.label(context) : action.label;
    return this.translate.instant(label);
  }

  protected run(action: ActionMenuItem<T[]>): void {
    if (this.isDisabled(action)) return;
    action.command(this.context());
  }

  protected onClear(): void {
    this.clear.emit();
  }

  protected readonly trackByLabel = (
    _: number,
    action: ActionMenuItem<T[]>
  ): string =>
    typeof action.label === 'function'
      ? action.label(this.context())
      : action.label;

  private _resolve<V>(
    value: V | ((context: T[]) => V) | undefined,
    context: T[],
    fallback: V
  ): V {
    if (value === undefined) return fallback;
    return typeof value === 'function'
      ? (value as (context: T[]) => V)(context)
      : value;
  }
}
